import { useCallback, useEffect, useRef, useState } from 'react';
import type { Game, GameReviewListKind, GameReviewListRequest } from '../../../../types/features/games';
import missingGamesService from '../services/missingGamesService';

const loadMessages: Record<GameReviewListKind, string> = {
  plu_verification_pending: 'Failed to load PLU verification pending games',
  missing_images: 'Failed to load games missing images',
};

const loadGameReviewList = (kind: GameReviewListKind, params: GameReviewListRequest) => (
  kind === 'plu_verification_pending'
    ? missingGamesService.getPluVerificationPendingGames()
    : missingGamesService.getMissingImageGames(params)
);

export const useGameReviewGames = (
  kind: GameReviewListKind,
  enabled: boolean,
  params: GameReviewListRequest = {},
) => {
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const requestIdRef = useRef(0);
  const requestPage = params.page;
  const requestState = params.state;
  const requestSearch = params.search;

  const loadGames = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError(null);

    try {
      const response = await loadGameReviewList(kind, {
        page: requestPage,
        state: requestState,
        search: requestSearch,
      });

      if (requestId !== requestIdRef.current) {
        return;
      }

      if (!response.success) {
        setGames([]);
        setTotal(0);
        setError(response.error || loadMessages[kind]);
        return;
      }

      setGames(response.data || []);
      setTotal(response.total_count ?? response.count ?? response.data?.length ?? 0);
    } catch (loadError) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      setGames([]);
      setTotal(0);
      setError(loadError instanceof Error ? loadError.message : loadMessages[kind]);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [kind, requestPage, requestState, requestSearch]);

  useEffect(() => {
    if (!enabled) {
      requestIdRef.current += 1;
      setLoading(false);
      return undefined;
    }

    void loadGames();

    return () => {
      requestIdRef.current += 1;
    };
  }, [enabled, loadGames]);

  return { games, loading, error, reload: loadGames, total };
};
